import { Box, Chip, Skeleton, Typography, useTheme } from '@mui/material';
import zeplinColor from '../../../../theme/Color';
import AsanaViewTask from '../AsanaViewTask/AsanaViewTask';
import { useEffect, useState } from 'react';
import CustomPopOver from '../../../shared/CustomPopOver/CustomPopOver';
import { AsanaService } from '../../../../service/asana.service';
import { LightTooltip } from '../../../shared/CustomTooltip/CustomTooltip';

/*
 * File: TaskCardUi.tsx
 * Project: a-a
 * File Created: Sunday, 11th May 2025 4:09:40 pm
 * Author: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Last Modified: Friday, 13th June 2025 6:00:25 pm
 * Modified By: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Copyright 2020 - 2025 Mutual Mobile, Mutual Mobile
 */
export const TaskCardUI = ({
  task,
  isDragging,
  loading,
  setDrawerState,
  drawerState,
  assigneeTask,
  setAssigneeTask,
  getTask,
}: {
  task: any;
  isDragging: boolean;
  loading: boolean;
  setDrawerState: React.Dispatch<React.SetStateAction<boolean>>;
  drawerState: boolean;
  assigneeTask: string;
  setAssigneeTask?: React.Dispatch<React.SetStateAction<string>>;
  getTask: ((clear?: boolean) => Promise<void>) | undefined;
}) => {
  const theme = useTheme();

  return (
    <>
      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}
        // onClick={() => setDrawerState(true)}
      >
        {' '}
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          {loading ? (
            <Skeleton
              variant='text'
              sx={{
                fontSize: theme.typography.body2,
                width: '100%',
              }}
            />
          ) : task?.title?.length < 80 ? (
            <Typography variant='body2'>{isDragging ? '' : task?.title}</Typography>
          ) : (
            <LightTooltip
              title={
                <Typography variant='body2' color={zeplinColor.Neutral1}>
                  {task?.title}
                </Typography>
              }
              placement='bottom-end'
              style={{
                backgroundColor: zeplinColor.BasicBlack,
                borderRadius: '8px',
                padding: '12px',
              }}
              // sx={{ [`& .${tooltipClasses.tooltip}`]: { backgroundColor: zeplinColor.Neutral1 } }}
            >
              <Typography variant='body2'>
                {task?.title?.slice(0, 78)}
                {task?.title?.length > 80 ? '...' : ''}
              </Typography>
            </LightTooltip>
          )}
          {task?.completed ? (
            <LightTooltip
              title={
                <Typography variant='body3' color={zeplinColor.Neutral1}>
                  Mark as incomplete
                </Typography>
              }
              placement='bottom'
              style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
            >
              <img
                src={'/assets/completed.png'}
                width={'24px'}
                height={'24px'}
                style={{ borderRadius: '100px', objectFit: 'cover' }}
                alt='completed'
              />
            </LightTooltip>
          ) : (
            <LightTooltip
              title={
                <Typography variant='body3' color={zeplinColor.Neutral1}>
                  Mark as complete
                </Typography>
              }
              placement='bottom'
              style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
            >
              <img
                src={'/assets/mark-as-read.png'}
                width={'24px'}
                height={'24px'}
                style={{ borderRadius: '100px', objectFit: 'cover' }}
                alt='not-completed'
              />
            </LightTooltip>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: '8px' }}>
          {loading ? (
            <Skeleton
              variant='text'
              sx={{
                fontSize: theme.typography.body2,
                width: '100%',
              }}
            />
          ) : isDragging ? (
            ''
          ) : task?.priority ? (
            <Chip
              label={<Typography variant='label1'>{task?.priority}</Typography>}
              sx={{
                bgcolor: task?.priorityColor,
                color: zeplinColor.Neutral1,
                '&.MuiChip-root': {
                  height: '22px',
                  padding: '4px 8px',
                  borderRadius: '50px',
                },
                '& .MuiChip-label': {
                  padding: 0,
                },
              }}
            />
          ) : (
            <Typography variant='body2'>NA</Typography>
          )}
          {loading ? (
            <Skeleton
              variant='text'
              sx={{
                fontSize: theme.typography.body2,
                width: '100%',
              }}
            />
          ) : isDragging ? (
            ''
          ) : task?.status ? (
            <Chip
              label={<Typography variant='label1'>{task?.status}</Typography>}
              sx={{
                bgcolor: task?.statusColor,
                color: zeplinColor.Neutral1,
                '&.MuiChip-root': {
                  height: '22px',
                  padding: '4px 8px',
                  borderRadius: '50px',
                },
                '& .MuiChip-label': {
                  padding: 0,
                },
              }}
            />
          ) : (
            <Typography variant='body2'>NA</Typography>
          )}
        </Box>
      </Box>
      {isDragging ? (
        ''
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          {loading ? (
            <Skeleton
              variant='text'
              sx={{
                fontSize: theme.typography.body2,
                width: '100%',
              }}
            />
          ) : (
            <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <Box
                sx={{
                  borderRadius: '100px',
                  padding: '10px',
                  width: '26px',
                  height: '26px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: task?.assigneePhoto ? zeplinColor.Brand1 : zeplinColor.Neutral8,
                }}
              >
                {!task?.assignee ? (
                  <img
                    src={'/assets/no-assignee.png'}
                    width={'26px'}
                    height={'26px'}
                    style={{ borderRadius: '100px', objectFit: 'cover' }}
                    alt='no-assignee-photo'
                  />
                ) : task?.assigneePhoto ? (
                  <img
                    src={task?.assigneePhoto}
                    width={'26px'}
                    height={'26px'}
                    style={{ borderRadius: '100px', objectFit: 'cover' }}
                    alt='asana-task-assignee-photo'
                  />
                ) : (
                  <Typography variant='label1' color={zeplinColor.Neutral1}>
                    {task?.assignee
                      ?.split(' ')
                      .map((value: any, index: number) =>
                        index === 0 || index === task?.assignee?.split(' ').length - 1
                          ? value[0]
                          : '',
                      )}
                  </Typography>
                )}
              </Box>
              {!task?.assignee?.length ? (
                ''
              ) : task?.assignee?.length < 15 ? (
                <Typography variant='body2'>{task?.assignee?.slice(0, 15)}</Typography>
              ) : (
                <LightTooltip
                  title={
                    <Typography variant='body3' color={zeplinColor.Neutral1}>
                      {task?.assignee}
                    </Typography>
                  }
                  placement='bottom-end'
                  style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
                >
                  <Typography variant='body2'>{task?.assignee?.slice(0, 14)}...</Typography>
                </LightTooltip>
              )}
            </Box>
          )}
          {loading ? (
            <Skeleton
              variant='text'
              sx={{
                fontSize: theme.typography.body2,
                width: '100%',
                // Optionally adjust other styles here
              }}
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                justifyContent: 'flex-end',
              }}
            >
              <img src='/assets/calendar.svg' width={18} height={18} style={{}} />
              {task?.dueDate ? (
                <>
                  <Typography
                    variant='body3'
                    color={task?.overdue ? zeplinColor.Error : zeplinColor.BasicBlack}
                  >
                    {new Date(task?.dueDate)
                      ? new Date(task?.dueDate)
                          .toLocaleString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                          })
                          .replace(/(\s)(\w{3})(\s)(\d{4})/, '$1$2, $4')
                      : ''}
                  </Typography>
                  {task?.overdue && (
                    <LightTooltip
                      title={
                        <Typography variant='body3' color={zeplinColor.Neutral1}>
                          Overdue
                        </Typography>
                      }
                      placement='bottom'
                      style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
                    >
                      <img src='/assets/overdue-icon.svg' width={18} height={18} style={{}} />
                    </LightTooltip>
                  )}
                </>
              ) : (
                'NA'
              )}
            </Box>
          )}
        </Box>
      )}

      {assigneeTask === task?.id && (
        <AsanaViewTask
          setDrawerState={setDrawerState}
          drawerState={drawerState}
          selectedData={task}
          getTask={getTask}
          // setDeleted={setDeleted}
          // setEditDone={setEdit}
          // setSelected={setSelected}
        />
      )}
    </>
  );
};
